import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, GitBranch, Send, Search } from "lucide-react";

/**
 * Bulk retroactive linkage — from a parent contract, pick many orphan contracts
 * (no `parent_contract_id`) and attach them all at once. Complements
 * <LinkParentModal> which is single-child, child-side.
 */
export default function LinkChildrenModal({ open, parentContract, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picks, setPicks] = useState({}); // {id: {relation, contract_number}}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ(""); setResults([]); setPicks({});
  }, [open, parentContract?.id]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const c = new AbortController();
    const t = setTimeout(() => {
      api.get(`/org/contracts/search?q=${encodeURIComponent(q.trim())}&status=all`, { signal: c.signal })
        .then(r => setResults((r.data || [])
                              .filter(x => x.id !== parentContract?.id && !x.parent_contract_id)
                              .slice(0, 12)))
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(t); c.abort(); };
  }, [q, open, parentContract?.id]);

  if (!open || !parentContract) return null;

  const toggle = (r) => setPicks(prev => {
    const next = { ...prev };
    if (next[r.id]) delete next[r.id];
    else next[r.id] = { relation: "addendum", contract_number: r.contract_number, vendor: r.vendor_legal_name };
    return next;
  });
  const setRel = (id, rel) => setPicks(prev => ({ ...prev, [id]: { ...prev[id], relation: rel } }));

  const ids = Object.keys(picks);

  const submit = async () => {
    if (ids.length === 0) return toast.error("Pick at least one child contract");
    setBusy(true);
    try {
      let ok = 0, fail = 0;
      for (const cid of ids) {
        try {
          await api.post(`/contracts/${cid}/link-parent`, {
            parent_contract_id: parentContract.id, relation: picks[cid].relation,
          });
          ok++;
        } catch { fail++; }
      }
      toast.success(`Linked ${ok} contract${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
      onDone && onDone(); onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center" onClick={onClose}
         data-testid="link-children-modal">
      <div className="bg-white rounded-md w-[620px] max-w-[92vw] shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-[#005289]" />
          <div className="font-bold text-slate-900">Link children to #{parentContract.contract_number}</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500 italic">
            Bulk-attach orphan addendums / renewals / amendments to <b>#{parentContract.contract_number}</b>. Only contracts without an existing parent are shown.
          </div>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Search orphans</span>
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={e => setQ(e.target.value)}
                     data-testid="link-children-search"
                     placeholder="Vendor name, contract number…"
                     className="w-full h-9 pl-7 pr-3 text-sm border border-slate-200 rounded-md" />
            </div>
          </label>
          <div className="max-h-72 overflow-y-auto border border-slate-100 rounded" data-testid="link-children-results">
            {q.trim().length < 2 && <div className="p-3 text-xs text-slate-400 italic text-center">Type to search orphan contracts…</div>}
            {q.trim().length >= 2 && results.length === 0 && <div className="p-3 text-xs text-slate-400 italic text-center">No orphan matches — everything found already has a parent.</div>}
            {results.map(r => (
              <div key={r.id} className={`px-3 py-2 border-b border-slate-100 last:border-0 flex items-center gap-2 ${picks[r.id] ? "bg-[#005289]/5" : "hover:bg-slate-50"}`}>
                <input type="checkbox" checked={!!picks[r.id]} onChange={() => toggle(r)}
                       data-testid={`link-children-check-${r.contract_number}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-slate-500">#{r.contract_number}</span>
                    <span className="font-semibold text-slate-900 truncate">{r.vendor_legal_name}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{r.folder_path} · {(r.stage || "").replace(/_/g,' ')}</div>
                </div>
                {picks[r.id] && (
                  <select value={picks[r.id].relation} onChange={e => setRel(r.id, e.target.value)}
                          className="h-7 text-[11px] border border-slate-200 rounded px-1">
                    <option value="addendum">Addendum</option>
                    <option value="renewal">Renewal</option>
                    <option value="amendment">Amendment</option>
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="link-children-count">{ids.length} selected</span>
          <button onClick={onClose} className="ml-auto h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={busy || ids.length === 0} data-testid="link-children-submit"
                  className="h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Linking…" : `Link ${ids.length || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
